import React, { useCallback, useEffect, useState } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Receipt } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { OrganisationWithDetails } from '../index'
import SubscriptionsList from './SubscriptionsList'

/**
 * What a B2C organisation's users have bought. Read-only by design: Georgia offers no
 * refund, top-up or adjustment. A refund or a dispute is the partner's own action in
 * their Stripe Dashboard, and shows up here because the webhook recorded it.
 */

interface MinutePurchase {
  id: number
  userEmail: string | null
  userFirstName: string | null
  userLastName: string | null
  moduleName: string | null
  packageName: string
  seconds: number
  amount: number
  currency: string
  status: string
  refundedAmount: number
  disputeStatus: string | null
  stripePaymentIntentId: string | null
  livemode: boolean
  createdAt: string
}

const GET_PURCHASES = gql`
  query getOrganisationMinutePurchases($organisationId: Int!, $limit: Int, $offset: Int) {
    getOrganisationMinutePurchases(organisationId: $organisationId, limit: $limit, offset: $offset) {
      totalCount
      purchases {
        id
        userEmail
        userFirstName
        userLastName
        moduleName
        packageName
        seconds
        amount
        currency
        status
        refundedAmount
        disputeStatus
        stripePaymentIntentId
        livemode
        createdAt
      }
    }
  }
`

const PAGE_SIZE = 25

const STATUS_CLASS: Record<string, string> = {
  paid: 'bg-green-100 text-green-800',
  pending: 'bg-blue-100 text-blue-800',
  expired: 'bg-gray-200 text-gray-600',
  failed: 'bg-red-100 text-red-800',
}

const money = (amount: number, currency: string) => `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`

const PurchasesTab: React.FC<{ organisation: OrganisationWithDetails | null; loading: boolean }> = ({ organisation, loading }) => {
  const [purchases, setPurchases] = useState<MinutePurchase[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loadingPage, setLoadingPage] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const organisationId = organisation?.id

  const fetchPage = useCallback(async () => {
    if (!organisationId) return
    try {
      setLoadingPage(true)
      setError(null)
      const response = await authorizedGraphQLRequest<{
        getOrganisationMinutePurchases: { purchases: MinutePurchase[]; totalCount: number }
      }>(GET_PURCHASES, { organisationId, limit: PAGE_SIZE, offset })
      setPurchases(response.getOrganisationMinutePurchases?.purchases || [])
      setTotalCount(response.getOrganisationMinutePurchases?.totalCount || 0)
    } catch (err: any) {
      setError(err?.response?.errors?.[0]?.message || 'Failed to load purchases')
    } finally {
      setLoadingPage(false)
    }
  }, [organisationId, offset])

  useEffect(() => {
    fetchPage()
  }, [fetchPage])

  // A different organisation starts at the first page again.
  useEffect(() => {
    setOffset(0)
  }, [organisationId])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!organisation) {
    return <div className="text-center text-gray-500 py-12">No organisation selected</div>
  }

  const page = Math.floor(offset / PAGE_SIZE) + 1
  const pages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  return (
    <div className="space-y-4">
      <SubscriptionsList organisationId={organisation.id} />

      <div className="flex justify-between items-center pt-4 border-t border-gray-100">
        <h4 className="font-semibold text-gray-800">Top-up purchases ({totalCount})</h4>
        <p className="text-xs text-gray-500">Refunds are made by the partner in their own Stripe Dashboard.</p>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 text-sm text-red-700 border border-red-100">{error}</div>}

      {purchases.length === 0 && !loadingPage ? (
        <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-xl">
          <Receipt className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No purchases yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-4 font-semibold">Date</th>
                <th className="py-2 pr-4 font-semibold">User</th>
                <th className="py-2 pr-4 font-semibold">Module</th>
                <th className="py-2 pr-4 font-semibold">Package</th>
                <th className="py-2 pr-4 font-semibold">Amount</th>
                <th className="py-2 pr-4 font-semibold">Status</th>
                <th className="py-2 pr-4 font-semibold">Refunded</th>
                <th className="py-2 pr-4 font-semibold">Dispute</th>
                <th className="py-2 font-semibold">Stripe payment</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((purchase) => (
                <tr key={purchase.id} className="border-b border-gray-100 last:border-0">
                  <td className="py-2 pr-4 whitespace-nowrap text-gray-700">{new Date(purchase.createdAt).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-gray-700">
                    <span className="block truncate max-w-48">{[purchase.userFirstName, purchase.userLastName].filter(Boolean).join(' ') || purchase.userEmail}</span>
                  </td>
                  <td className="py-2 pr-4 text-gray-700">{purchase.moduleName || '—'}</td>
                  <td className="py-2 pr-4 text-gray-700">
                    {purchase.packageName}
                    <span className="block text-xs text-gray-500">{Math.round(purchase.seconds / 60)} min</span>
                  </td>
                  <td className="py-2 pr-4 tabular-nums text-gray-700">{money(purchase.amount, purchase.currency)}</td>
                  <td className="py-2 pr-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_CLASS[purchase.status] || 'bg-gray-100 text-gray-600'}`}>{purchase.status}</span>
                    {purchase.livemode ? null : <span className="ml-1 text-xs text-gray-400">test</span>}
                  </td>
                  <td className="py-2 pr-4 tabular-nums text-gray-700">{purchase.refundedAmount > 0 ? money(purchase.refundedAmount, purchase.currency) : '—'}</td>
                  <td className="py-2 pr-4 text-gray-700">{purchase.disputeStatus || '—'}</td>
                  <td className="py-2 font-mono text-xs text-gray-500">{purchase.stripePaymentIntentId || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            Page {page} of {pages}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={offset === 0 || loadingPage} onClick={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))}>
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled={offset + PAGE_SIZE >= totalCount || loadingPage} onClick={() => setOffset((current) => current + PAGE_SIZE)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default PurchasesTab
